import React, { useEffect, useMemo } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import { Alert, Button, Spinner } from 'react-bootstrap'
import { useArticlesState } from '../../../stores/Configuration/ArticlesProvider'
import { columns, numberFilter } from './filterConfig'
import UniversalTableWrapperTest from '../../Table/UniversalTableWrapperTest'
import TableProvider, { useTableState } from '../../Table/TableProvider'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import { useTranslation } from 'react-i18next'
import OverflowTooltip from '../../../components/OverflowTooltip.jsx'

const defaultSorted = [
  {
    dataField: 'number',
    order: 'desc',
  },
]

const ArticlesTableInner = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    articles,
    setPageOffset,
    setPageLimit,
    filter,
    setFilter,
    selectedRow,
    setShowAllArticles,
    setSorting,
    tableIsLoading,
    resetFilter,
    filterArticle,
    setFilterArticle,
    totalCount,
    selectAll,
    allCheckBoxesSelected,
    allSelectedLoading,
    setInitialTableId,
    initialTableId,
    setSelectedCheckBoxes,
    showAllArticles,
  } = useArticlesState()

  const { t } = useTranslation()

  const { tableRef, paginationOptions, currentPage, totalPages } =
    useTableState()

  useMountEffect(() => {
    if (tableId !== initialTableId) {
      setInitialTableId(tableId)
    }
    setSelectedCheckBoxes([])
  })

  useEffect(() => {
    //after Creating new Article auto output him
    if (!!filterArticle) {
      setTimeout(() => {
        resetFilter()
        //set text in number searchbar
        numberFilter(filterArticle)
        //set filter + trigger call
        setFilter((prev) => ({
          ...prev,
          'filter.number': filterArticle,
        }))
      }, 50)
      setFilterArticle(null)
    }
  }, [filterArticle, setFilterArticle, resetFilter, setFilter])

  const handleTableChange = (type, newState) => {
    setPageLimit(newState.sizePerPage)
    if (type === 'filter') {
      setShowAllArticles(false)
      const { filters } = newState
      setFilter((prev) => ({
        ...prev,
        'filter.number': filters.number ? filters.number.filterVal : '',
        'filter.internalName': filters.internalName
          ? filters.internalName.filterVal
          : '',
        'filter.classInternalName': filters['class.internalName']
          ? filters['class.internalName'].filterVal
          : '',
      }))
    }

    if (type === 'sort') {
      const { sortOrder, sortField } = newState
      setSorting(`${sortOrder === 'asc' ? '-' : ''}${sortField}`)
    }
    setPageOffset(
      newState.page === 1
        ? 0
        : newState.sizePerPage * newState.page - newState.sizePerPage
    )
  }
  const preparedColumns = useMemo(() => {
    const tmpColumns = [...columns]
    const fieldsToRemove = ['externalName', 'class.externalName']

    const translationOverrides = {
      internalName: 'articleAdminTable:articleName',
      'class.internalName': 'articleAdminTable:articleClass',
    }
    const filteredColumns = tmpColumns
      .filter((col) => !fieldsToRemove.includes(col.dataField))
      .map((col) => {
        const newCol = { ...col }
        if (newCol.dataField === 'actions') {
          newCol.headerFormatter = () => (
            <Button
              variant={showAllArticles ? 'secondary' : 'outline-secondary'}
              className='w-100'
              onClick={() => {
                setShowAllArticles((prevState) => !prevState)
              }}
            >
              {t('articleAdminTable:showAllBtn')}
            </Button>
          )

          return newCol
        }

        const translationKey =
          translationOverrides[newCol.dataField] || newCol.text
        newCol.text = t(translationKey)

        newCol.headerFormatter = (column, colIndex, components) => {
          switch (newCol.dataField) {
            case 'checkBox':
              return (
                <span>
                  {allSelectedLoading ? (
                    <Spinner animation='border' variant='primary' size='sm' />
                  ) : (
                    <input
                      type='checkbox'
                      id='checkbox_header'
                      name='scales'
                      checked={allCheckBoxesSelected}
                      onChange={selectAll}
                    ></input>
                  )}
                </span>
              )
            default:
              return (
                <>
                  <OverflowTooltip title={column.text}>
                    {column.text}
                    {components.sortElement}
                    <div className='d-flex align-items-end'>
                      {components.filterElement}
                    </div>
                  </OverflowTooltip>
                </>
              )
          }
        }
        return newCol
      })

    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.text = t(tmpColumn.text)
    })

    const ignoreColumns = () => {
      const result = []
      if (noActions) result.push('actions')
      if (!withCheckbox) result.push('checkBox')
      return result
    }

    const finalColumns = filteredColumns.filter(
      (col) => !ignoreColumns().includes(col.dataField)
    )

    finalColumns.forEach((col) => {
      col.text = t(col.text)
    })

    return finalColumns
  }, [
    allSelectedLoading,
    allCheckBoxesSelected,
    selectAll,
    noActions,
    withCheckbox,
    t,
    setShowAllArticles,
    showAllArticles,
  ])

  const getChosenFilters = () => {
    var message = []
    if (filter) {
      message = Object.keys(filter).filter((key) => filter[key] !== '')
    }
    return message
  }
  const noDataIndicationMessage = () => {
    const chosenFilters = getChosenFilters()
    const alertMessage = t('articleAdminTable:alertMsg')
    if (totalCount === 0 && chosenFilters.length > 0 && !tableIsLoading) {
      return (
        <div className='no-data-placeholder-alert'>
          <Alert variant='info'>{alertMessage}</Alert>
        </div>
      )
    } else {
      return (
        <div className='no-data-placeholder'>
          {t('articleAdminTable:pleaseSearchArticle')}
        </div>
      )
    }
  }

  return (
    <UniversalTableWrapperTest
      tableIsLoading={tableIsLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      customBtn={true}
    >
      <BootstrapTable
        ref={tableRef}
        remote
        loading={tableIsLoading}
        noDataIndication={() => noDataIndicationMessage()}
        onTableChange={handleTableChange}
        bootstrap4
        keyField='id'
        hover
        data={articles}
        columns={preparedColumns}
        defaultSorted={defaultSorted}
        rowEvents={{ onClick: onRowClick }}
        rowClasses={(row, rowIndex) => {
          let classes = []
          if (row === selectedRow) {
            classes.push('active')
          }
          if (row.isDeleted) {
            classes.push('is-deleted')
          }
          return classes.join(' ')
        }}
        pagination={paginationFactory(paginationOptions)}
        filter={filterFactory()}
        bordered={false}
      />
    </UniversalTableWrapperTest>
  )
}

const ArticlesTable = ({ noActions, onRowClick, withCheckbox, tableId }) => {
  const {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  } = useArticlesState()

  const providerSettings = {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  }
  return (
    <TableProvider {...providerSettings}>
      <ArticlesTableInner
        noActions={noActions}
        withCheckbox={withCheckbox}
        onRowClick={onRowClick}
        tableId={tableId}
      />
    </TableProvider>
  )
}

export default ArticlesTable
